// Angular
import { Observable, combineLatest, map, of, takeUntil } from 'rxjs';
import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpBackend, HttpHeaders } from '@angular/common/http';

// Libraries

import { Select } from '@ngxs/store';

// Models
import { User } from '../models/user.model';
import { Permission } from '../models/permission.model';
import { Role } from '../models/role.model';
import { RegisterResponse } from '../models/register-response.model';
import { CompanyInviteDto } from '../models/company-invite-dto.model';
import { PasswordChangeDto } from '../models/password-change-dto.model';
import { UserModel } from '../../../shared/models/user-model.model';
import { AppCleanup } from '../../../shared/models';

// Store
import { AuthState } from '../state/auth.state';
import { UserAuthState } from '../state/user-auth.state';

// Enums
import { RolesMap, Roles } from '../models/roles.enum';

// Environment
import { environment } from '../../../../environments/environment';

@Injectable()
export class AuthService extends AppCleanup {
	@Select(AuthState.isLoggedIn) isLoggedIn$: Observable<boolean>;
	@Select(UserAuthState.currentUser) currentUser$: Observable<UserModel>;
	@Select(UserAuthState.currentUserRoleIds) currentUserRoleIds$: Observable<number[]>;
	authUrl: string;

	constructor(
		private http: HttpClient,
		private handler: HttpBackend
	) {
		super();
		this.authUrl = `${this.apiUrl}api/auth`;
	}

	login(userName: string, password: string): Observable<UserModel> {
		return this.http.post<UserModel>(`${this.authUrl}/login`, { userName, password });
	}

	getUserByHardcodedToken(): Observable<UserModel> {
		let newHttp = new HttpClient(this.handler);

		const httpOptions = {
			headers: new HttpHeaders({
				'Content-Type': 'application/json',
				Authorization: `Bearer ${environment.hardCodedToken}`,
			}),
		};
		return newHttp.get<UserModel>(`${this.authUrl}/users`, httpOptions);
	}

	getUserByToken(isViewer: boolean = false): Observable<UserModel> {
		return this.http.get<UserModel>(`${this.authUrl}/users`);
	}

	register(user: UserModel): Observable<RegisterResponse> {
		return this.http.post<RegisterResponse>(`${this.authUrl}/register`, user).pipe(
			map((res: RegisterResponse) => {
				return res;
			})
		);
	}

	registerExternal(user: UserModel, companyAdmin: boolean): Observable<RegisterResponse> {
		let admin = companyAdmin ? 'yes' : 'no';

		return this.http.post<RegisterResponse>(`${this.authUrl}/register/external?admin=${admin}`, user).pipe(
			map((res: RegisterResponse) => {
				return res;
			})
		);
	}

	registerInvite(companyInvite: CompanyInviteDto): Observable<number> {
		return this.http.post<number>(`${this.authUrl}/register/invite`, companyInvite);
	}

	/*
	 * Submit forgot password request
	 *
	 * @param {string} email
	 * @returns {Observable<any>}
	 */
	requestPassword(email: string): Observable<any> {
		return this.http.get(`${this.authUrl}` + '/forgot?email=' + email);
	}

	//getAllUsers(): Observable<User[]> {
	//	return this.http.get<User[]>(`${this.authUrl}/users`);
	//   }

	getUserById(userId: number): Observable<User> {
		return this.http.get<User>(`${this.authUrl}/users/${userId}`);
	}

	// DELETE => delete the user from the server
	deleteUser(userId: number) {
		const url = `${this.authUrl}/users/${userId}`;
		return this.http.delete(url);
	}

	// UPDATE => PUT: update the user on the server
	updateUser(_user: User): Observable<any> {
		return this.http.put(`${this.authUrl}/users`, _user);
	}

	// CREATE =>  POST: add a new user to the server
	createUser(user: User): Observable<User> {
		return this.http.post<User>(`${this.authUrl}/users`, user);
	}

	// Permission
	getAllPermissions(): Observable<Permission[]> {
		return this.http.get<Permission[]>(`${this.authUrl}/permissions`);
	}

	getRolePermissions(roleId: number): Observable<Permission[]> {
		return this.http.get<Permission[]>(`${this.authUrl}/permisssions/getRolePermission?=${roleId}`);
	}

	// Roles
	getAllRoles(): Observable<Role[]> {
		return this.http.get<Role[]>(`${this.authUrl}/roles`);
	}

	getRoleById(roleId: number): Observable<Role> {
		return this.http.get<Role>(`${this.authUrl}/roles/${roleId}`);
	}

	// CREATE =>  POST: add a new role to the server
	createRole(role: Role): Observable<Role> {
		return this.http.post<Role>(`${this.authUrl}/roles`, role);
	}

	// UPDATE => PUT: update the role on the server
	updateRole(role: Role): Observable<any> {
		return this.http.put(`${this.authUrl}/roles`, role);
	}

	// DELETE => delete the role from the server
	deleteRole(roleId: number): Observable<Role> {
		return this.http.delete<Role>(`${this.authUrl}/roles/${roleId}`);
	}

	// Check Role Before deletion
	isRoleAssignedToUsers(roleId: number): Observable<boolean> {
		return this.http.get<boolean>(`${this.authUrl}/roles/checkIsRollAssignedToUser?roleId=${roleId}`);
	}

	checkAccess(roles?: string[], failureCallback?: Function, successCallback?: Function, eulaCallback?: Function): Observable<boolean> {
		return combineLatest(this.isLoggedIn$, this.currentUserRoleIds$, this.currentUser$, (loggedIn, userRoles, currentUser) => {
			if (this.isViewer) {
				if (successCallback) successCallback();

				return true;
			}

			if (!loggedIn) {
				if (failureCallback) failureCallback();

				return false;
			}

			if (roles && roles.filter((r) => r in Roles && userRoles.includes(RolesMap[r])).length === 0) {
				if (failureCallback) failureCallback();

				return false;
			}

			if (eulaCallback && currentUser && !currentUser.confirmedEula) {
				eulaCallback();

				return false;
			}

			if (successCallback) successCallback();

			return true;
		});
	}

	getHighestRole(): Observable<any> {
		return new Observable((obs) => {
			return this.currentUserRoleIds$.pipe(takeUntil(this.destroy)).subscribe((userRoles) => {
				if (userRoles && userRoles.length > 1) {
					obs.next(Math.max(...userRoles));
				} else {
					obs.next(null);
				}
			});
		});
	}

	isLoggedIn(roles?: string[], failureCallback?: Function, successCallback?: Function, eulaCallback?: Function): Observable<boolean> {
		return new Observable((obs) => {
			return this.isLoggedIn$.pipe(takeUntil(this.destroy)).subscribe((loggedIn) => {
				obs.next(loggedIn);
			});
		});
	}

	// isLoggedIn(roles?: string[], failureCallback?: Function, successCallback?: Function, eulaCallback?: Function): Observable<boolean> {
	// 	return combineLatest(
	// 		this.store.pipe(select(isLoggedIn)),
	// 		this.store.pipe(select(currentUserRoleIds)),
	// 		this.store.pipe(select(currentUser)),
	// 		(loggedIn, userRoles, currentUser) => {
	// 			if (this.isViewer) {
	// 				if (successCallback)
	// 					successCallback();

	// 				return true;
	// 			}

	// 			if (!loggedIn) {
	// 				if (failureCallback)
	// 					failureCallback();

	// 				return false;
	// 			}

	// 			if (roles && roles.filter(r => (r in Roles) && userRoles.includes(RolesMap[r])).length === 0) {
	// 				if (failureCallback)
	// 					failureCallback();

	// 				return false;
	// 			}

	// 			if (eulaCallback && currentUser && !currentUser.confirmedEula) {
	// 				eulaCallback();

	// 				return false;
	// 			}

	// 			if (successCallback)
	// 				successCallback();

	// 			return true;
	// 		}
	// 	);

	confirmEmail() {
		return this.http.post<any>(`${this.authUrl}/confirm`, null);
	}

	agreeToEula() {
		return this.http.post<any>(`${this.authUrl}/eula`, null);
	}

	/*
	 * Handle Http operation that failed.
	 * Let the app continue.
	 *
	 * @param operation - name of the operation that failed
	 * @param result - optional value to return as the observable result
	 */
	private handleError<T>(operation = 'operation', result?: any) {
		return (error: any): Observable<any> => {
			// TODO: send the error to remote logging infrastructure
			console.error(error); // log to console instead

			// Let the app keep running by returning an empty result.
			return of(result);
		};
	}

	changePassword(passwordChange: PasswordChangeDto, token: string) {
		let newHttp = new HttpClient(this.handler);

		const httpOptions = {
			headers: new HttpHeaders({
				'Content-Type': 'application/json',
				Authorization: `Bearer ${token}`,
			}),
		};

		return newHttp.post<string>(`${this.authUrl}/change-password`, passwordChange, httpOptions);
	}

	changePasswordFromAdmin(passwordChange: PasswordChangeDto) {
		return this.http.post<string>(`${this.authUrl}/admin/change-password`, passwordChange);
	}

	deactiveFromAdmin(user: UserModel) {
		return this.http.put<string>(`${this.authUrl}/admin/deactivate`, user);
	}

	validateToken() {
		return this.http.get(`${this.authUrl}/validate`);
	}
}
